import { trim } from "lodash";
import isEmpty from "lodash/isEmpty";
import { RefObject, useCallback } from "react";
import { useCreateBillOfMaterial } from "../../../../api/billOfMaterialSummary";
import {
  usePostSupplierData,
  usePutIngredientData,
  usePutSupplierData,
  useUpdateInHouseSupllier
} from "../../../../api/ingredients";
import { useCreateSupplierDrawingRequest } from "../../../../api/supplier";
import { THEME } from "../../../../config/constants";
import {
  IngredientDataType,
  IngredientSource,
  IngredientType
} from "../../../../config/enums/IngredientType.enum";
import { SupplierType } from "../../../../config/enums/SupplierType.enum";
import { errorView, successMessage } from "../../../../helpers/ErrorHandler";
import {
  EnvironmentalContext,
  SupplierDataShape,
  SupplierLcaHandle
} from "../supplierRegister.types";
import {
  buildSupplierIngredientDetails,
  getIngredientAndSupplierId,
  getIngredientId,
  getSupplierId
} from "../supplierRegister.utils";

interface UseSupplierSaveParams {
  supplierData: SupplierDataShape;
  ingredientData?: Record<string, unknown>;
  effectiveDataType: string;
  viewEnvironmentalData: boolean;
  lcaRef?: RefObject<SupplierLcaHandle | null | undefined>;
  ingredientType: string;
  defaultType: string;
  country: number;
  specifyIngredientValue: string;
  produceType: string;
  farmingType: string;
  fieldType: string;
  supplierCountry: number;
  isSupplierAsUser: boolean;
  saveValues: (data: Record<string, unknown>, details?: Record<string, unknown>) => void;
  refetchSupplier?: () => void;
  setIngredientData?: (data: unknown) => void;
  saveIngredientImage?: (data: unknown) => void;
  setSupplierData: React.Dispatch<React.SetStateAction<SupplierDataShape>>;
  supplierCurrentData: (data?: SupplierDataShape) => void;
  checkCurrentSupplier?: (supplierId: unknown) => void;
  resetNewSupplier: () => void;
  setNewSupplier: (value: boolean) => void;
  setIsSupplierFormOpen: (value: boolean) => void;
  hideIngredientView: (value: boolean) => void;
  setIsSupplierAsUser: (value: boolean) => void;
}

export const useSupplierSave = ({
  supplierData,
  ingredientData,
  effectiveDataType,
  viewEnvironmentalData,
  lcaRef,
  ingredientType,
  defaultType,
  country,
  specifyIngredientValue,
  produceType,
  farmingType,
  fieldType,
  supplierCountry,
  isSupplierAsUser,
  saveValues,
  refetchSupplier,
  setIngredientData,
  saveIngredientImage,
  setSupplierData,
  supplierCurrentData,
  checkCurrentSupplier,
  resetNewSupplier,
  setNewSupplier,
  setIsSupplierFormOpen,
  hideIngredientView,
  setIsSupplierAsUser
}: UseSupplierSaveParams) => {
  const { mutateAsync: mutatePostSupplier } = usePostSupplierData();
  const { mutateAsync: mutatePutSupplier } = usePutSupplierData();
  const { mutateAsync: mutatePutIngredient } = usePutIngredientData();
  const { mutate: mutatePostSupplierDrawingRequest } = useCreateSupplierDrawingRequest();
  const { mutate: mutateCreateBillOfMaterial } = useCreateBillOfMaterial();
  const { mutate: updateInHouseSupplier } = useUpdateInHouseSupllier();

  const ingredientId = getIngredientId(ingredientData);
  const supplierId = getSupplierId(supplierData);

  const handleSupplierDrawingRequestSave = useCallback(
    (ingredientIdValue: string, supplierUserId: string) => {
      mutatePostSupplierDrawingRequest({
        ingredientId: ingredientIdValue,
        supplierUserId
      });
    },
    [mutatePostSupplierDrawingRequest]
  );

  const requestSupplierDrawingTool = useCallback(
    (data: Record<string, unknown>) => {
      if (data.is_supplier_as_user && effectiveDataType === IngredientDataType.SUPPLIER) {
        handleSupplierDrawingRequestSave(ingredientId as string, data.user_id as string);
      }
    },
    [effectiveDataType, handleSupplierDrawingRequestSave, ingredientId]
  );

  const updateLcaData = useCallback(
    (supplierLCAData?: Record<string, { measure: number; unit: string }>) => {
      const shouldSaveLcaData =
        effectiveDataType === IngredientDataType.CALCULATED_LCA &&
        viewEnvironmentalData &&
        Boolean(lcaRef?.current) &&
        Boolean(supplierLCAData);

      if (!shouldSaveLcaData || !ingredientId || !supplierId) {
        return;
      }

      const billOfMaterialData = {
        ingredientId,
        ingredientName: String((ingredientData as Record<string, unknown>)?.ingredient_name ?? ""),
        supplierId,
        supplierName: supplierData?.supplier_name ?? "",
        totalImpact: supplierLCAData ?? {}
      };
      mutateCreateBillOfMaterial(billOfMaterialData);
      successMessage("LCA data saved successfully");
    },
    [
      effectiveDataType,
      ingredientData,
      ingredientId,
      lcaRef,
      mutateCreateBillOfMaterial,
      supplierData?.supplier_name,
      supplierId,
      viewEnvironmentalData
    ]
  );

  const ingredientOnSuccess = useCallback(
    (data: Record<string, unknown>) => {
      successMessage("Ingredient update successfully");
      const nextIngredientData = {
        ingredient_name: data?.ingredient_name,
        ingredient_data: data
      };
      setIngredientData?.(nextIngredientData);
      saveIngredientImage?.(nextIngredientData);
    },
    [saveIngredientImage, setIngredientData]
  );

  const supplierOnSuccess = useCallback(
    (data: Record<string, unknown>) => {
      successMessage("Supplier update successfully");
      const supplierIngredient = data.supplierIngredientDetails as Record<string, unknown>;
      saveValues(data, supplierIngredient);
      refetchSupplier?.();
      requestSupplierDrawingTool(data);
    },
    [refetchSupplier, requestSupplierDrawingTool, saveValues]
  );

  const updateSupplierData = useCallback(
    (
      supplierDataUpdate: Record<string, unknown>,
      onSuccess?: () => void,
      onError?: (e: unknown) => void
    ) => {
      const supplierLCAData =
        effectiveDataType === IngredientDataType.CALCULATED_LCA
          ? lcaRef?.current?.supplierLCAData()
          : undefined;

      if (supplierDataUpdate?.supplier_type === SupplierType.Internal) {
        updateInHouseSupplier(supplierDataUpdate);
        onSuccess?.();
        return;
      }

      mutatePutSupplier(supplierDataUpdate, {
        onSuccess(data: unknown) {
          if (effectiveDataType === IngredientDataType.CALCULATED_LCA) {
            updateLcaData(supplierLCAData);
          }
          supplierOnSuccess(data as Record<string, unknown>);
          onSuccess?.();
        },
        onError
      });
    },
    [
      effectiveDataType,
      lcaRef,
      mutatePutSupplier,
      supplierOnSuccess,
      updateInHouseSupplier,
      updateLcaData
    ]
  );

  const updateIngredientData = useCallback(
    (
      supplierDataUpdate: Record<string, unknown>,
      onSuccess?: () => void,
      onError?: (e: unknown) => void
    ) => {
      const data = ingredientData as Record<string, unknown> | undefined;
      const ingredientDataRecord = data?.ingredient_data as Record<string, unknown> | undefined;
      const ingredient = {
        _id: ingredientId,
        ingredient_name: ingredientDataRecord?.ingredient_name || data?.ingredient_name,
        productIds: ingredientDataRecord?.productIds || data?.ingredientProductIds,
        ingredient_source:
          ingredientDataRecord?.ingredient_source || IngredientSource.BILL_OF_MATERIAL
      };

      mutatePutIngredient(ingredient, {
        onSuccess(data: unknown) {
          ingredientOnSuccess(data as Record<string, unknown>);
          updateSupplierData(supplierDataUpdate, onSuccess, onError);
        },
        onError
      });
    },
    [ingredientData, ingredientId, ingredientOnSuccess, mutatePutIngredient, updateSupplierData]
  );

  const saveData = useCallback(
    (
      formData: Record<string, unknown>,
      onSuccess?: () => void,
      onError?: (err: unknown) => void
    ) => {
      const { newSupplierProductIds, newSupplierIngIds } = getIngredientAndSupplierId(
        supplierData?.supplier_data,
        ingredientId
      );

      const envContext: EnvironmentalContext = {
        viewEnvironmentalData,
        dataType: effectiveDataType,
        ingredientType,
        country,
        specifyIngredientValue,
        produceType,
        farmingType,
        fieldType,
        defaultType,
        ingredientId
      };

      const supplierIngredientDetail = buildSupplierIngredientDetails(formData, envContext);

      // Extract supporting documents from formData
      const supportingDocuments = (formData.supportingDocuments as any[]) || [];

      const supplier: Record<string, unknown> = {
        _id: supplierId,
        supplier_name: supplierData?.supplier_name,
        supplier_type: supplierData?.supplier_type || SupplierType.External,
        productIds: newSupplierProductIds ?? [],
        ingredientIds: newSupplierIngIds ?? [],
        supplierIngredientDetails: supplierIngredientDetail
      };

      // Include supporting documents if available
      if (supportingDocuments.length > 0) {
        (supplier.supplierIngredientDetails as Record<string, unknown>).supportingDocuments =
          supportingDocuments;
      }

      updateIngredientData(supplier, onSuccess, onError);
    },
    [
      country,
      defaultType,
      effectiveDataType,
      farmingType,
      fieldType,
      ingredientId,
      ingredientType,
      produceType,
      specifyIngredientValue,
      supplierData,
      supplierId,
      updateIngredientData,
      viewEnvironmentalData
    ]
  );

  const handleSupplierSave = useCallback(
    (formData: Record<string, unknown>) => {
      if (effectiveDataType === IngredientDataType.GENERIC) {
        if (ingredientType === IngredientType.PROCESSED && isEmpty(specifyIngredientValue)) {
          errorView("Please Specify Ingredient");
          return;
        }
        if (ingredientType === IngredientType.RAW && isEmpty(produceType)) {
          errorView("Please select Produce Type");
          return;
        }
      }

      const dataToSubmit = {
        ...formData,
        supplier_name: trim(formData.supplier_name as string),
        supplier_contact_name: trim(formData.supplier_contact_name as string),
        supplier_email: trim(formData.supplier_email as string),
        supplier_country: supplierCountry,
        is_supplier_as_user: isSupplierAsUser,
        productIds: [],
        ingredientIds: [ingredientId],
        theme: THEME
      };

      mutatePostSupplier(dataToSubmit, {
        onSuccess(data: unknown) {
          const supplierResponse = data as Record<string, unknown>;
          const nextSupplierData: SupplierDataShape = {
            supplier_name: supplierResponse.supplier_name as string,
            supplier_data: supplierResponse
          };
          resetNewSupplier();
          supplierCurrentData(nextSupplierData);
          setSupplierData(nextSupplierData);
          checkCurrentSupplier?.(supplierResponse._id);
          successMessage("Supplier saved successfully");
          setNewSupplier(false);
          setIsSupplierFormOpen(false);
          refetchSupplier?.();
          hideIngredientView(false);
          setIsSupplierAsUser(false);
          if (
            supplierResponse.is_supplier_as_user &&
            effectiveDataType === IngredientDataType.SUPPLIER
          ) {
            handleSupplierDrawingRequestSave(
              ingredientId as string,
              supplierResponse.user_id as string
            );
          }
        }
      });
    },
    [
      checkCurrentSupplier,
      effectiveDataType,
      handleSupplierDrawingRequestSave,
      hideIngredientView,
      ingredientId,
      ingredientType,
      isSupplierAsUser,
      mutatePostSupplier,
      produceType,
      refetchSupplier,
      resetNewSupplier,
      setIsSupplierAsUser,
      setIsSupplierFormOpen,
      setNewSupplier,
      setSupplierData,
      specifyIngredientValue,
      supplierCountry,
      supplierCurrentData
    ]
  );

  return {
    saveData,
    handleSupplierSave,
    updateSupplierData,
    updateIngredientData
  };
};
